import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Save } from "lucide-react";
import { profileApi, type UpdateProfilePayload } from "@/api/profile";
import { Button, Input, Label, Textarea } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";

const BIO_MAX = 500;

// IANA zones for the Time zone picker. Prefer the runtime's full list;
// fall back to a small curated set on older engines without
// Intl.supportedValuesOf so the field still works.
const TIME_ZONES: string[] = (() => {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf;
    if (typeof supported === "function") return supported("timeZone");
  } catch {
    /* fall through to the curated list */
  }
  return [
    "America/Phoenix",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "UTC",
  ];
})();

export function ProfileEditPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const { refresh, user } = useAuth();

  const profile = useQuery({
    queryKey: ["profile", "me"],
    queryFn: () => profileApi.me(),
  });

  const [form, setForm] = useState<UpdateProfilePayload>({
    displayName: "",
    jobTitle: "",
    department: "",
    phoneNumber: "",
    bio: "",
    linkedInUrl: "",
    certifications: "",
    timeZone: "",
    signatureName: "",
    signatureTitle: "",
    signaturePhone: "",
    signatureEmail: "",
    signatureBlock: "",
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile.data) {
      const p = profile.data;
      setForm({
        displayName: p.displayName,
        jobTitle: p.jobTitle ?? "",
        department: p.department ?? "",
        phoneNumber: p.phoneNumber ?? "",
        bio: p.bio ?? "",
        linkedInUrl: p.linkedInUrl ?? "",
        certifications: p.certifications ?? "",
        // TimeZone lives on MeDto, not ProfileDto — seed it from the session.
        timeZone: user?.timeZone ?? "",
        signatureName: p.signatureName ?? "",
        signatureTitle: p.signatureTitle ?? "",
        signaturePhone: p.signaturePhone ?? "",
        signatureEmail: p.signatureEmail ?? "",
        signatureBlock: p.signatureBlock ?? "",
      });
      setPhotoPreview(p.photoUrl ?? null);
    }
  }, [profile.data, user?.timeZone]);

  const save = useMutation({
    mutationFn: async () => {
      // Upload photo first (if one was picked) so the subsequent refresh
      // sees the new avatar, then persist the text fields.
      if (photoFile) {
        setUploadingPhoto(true);
        try {
          await profileApi.uploadPhoto(photoFile);
        } finally {
          setUploadingPhoto(false);
          setPhotoFile(null);
        }
      }
      return profileApi.update(form);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["profile", "me"] });
      await refresh(); // update TopBar avatar + Dashboard CTA state
      await dialogs.alert({ title: "Profile saved", tone: "success", message: "Your biography is up to date." });
      navigate("/account/profile");
    },
    onError: (e: Error) => {
      void dialogs.alert({ title: "Save failed", tone: "error", message: e.message ?? "Please try again." });
    },
  });

  const onPickPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      void dialogs.alert({ title: "Not an image", tone: "error", message: "Pick a JPEG, PNG, WebP, or GIF." });
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const bioLen = (form.bio ?? "").length;
  const canSave = !save.isPending && !uploadingPhoto && (form.displayName?.trim().length ?? 0) > 0;

  return (
    <>
      <PageHeader
        eyebrow="Profile"
        title="Edit profile"
        description="Your biography, photo, and contact details are visible to every team member."
        actions={
          <div className="flex items-center gap-space-2">
            <Button variant="secondary" onClick={() => navigate(-1)} disabled={save.isPending}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!canSave}>
              <Save size={14} strokeWidth={1.5} aria-hidden />
              {save.isPending || uploadingPhoto ? "Saving…" : "Save"}
            </Button>
          </div>
        }
      />

      <div className="max-w-[800px] grid gap-space-5">
        {/* Photo */}
        <section className="flex flex-col items-center gap-space-3">
          <PhotoDisplay src={photoPreview} name={form.displayName ?? ""} size={120} />
          <div className="flex items-center gap-space-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              hidden
              onChange={onPickPhoto}
            />
            <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()}>
              <Camera size={14} strokeWidth={1.5} aria-hidden />
              {photoPreview ? "Change photo" : "Upload photo"}
            </Button>
          </div>
          <div className="text-caption text-gray-mid">
            JPEG, PNG, WebP, or GIF — up to 5 MB. Square images look best.
          </div>
        </section>

        <section className="grid gap-space-3">
          <div>
            <Label required>Display name</Label>
            <Input
              value={form.displayName ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <div>
              <Label>Title</Label>
              <Input
                value={form.jobTitle ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))}
                placeholder="Senior Underwriter"
              />
            </div>
            <div>
              <Label>Department</Label>
              <Input
                value={form.department ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <Label>Bio</Label>
              <span className={
                "text-caption " +
                (bioLen > BIO_MAX ? "text-status-error" : "text-gray-mid")
              }>
                {bioLen} / {BIO_MAX}
              </span>
            </div>
            <Textarea
              rows={4}
              maxLength={BIO_MAX}
              value={form.bio ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              placeholder="A short, punchy sentence or two — how you help Phoenix clients."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phoneNumber ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                placeholder="+1 212 555 0101"
              />
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input
                value={form.linkedInUrl ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, linkedInUrl: e.target.value }))}
                placeholder="https://linkedin.com/in/you"
              />
            </div>
          </div>

          <div>
            <Label>Certifications</Label>
            <Input
              value={form.certifications ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, certifications: e.target.value }))}
              placeholder="CPA, Series 65"
            />
          </div>

          <div>
            <Label>Time zone</Label>
            <select
              value={form.timeZone ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, timeZone: e.target.value }))}
              className="w-full h-10 px-space-3 border border-border-light rounded-md font-body text-small bg-white-pure"
            >
              {/* Keep the saved value selectable even if the runtime's list omits it. */}
              {form.timeZone && !TIME_ZONES.includes(form.timeZone) && (
                <option value={form.timeZone}>{form.timeZone}</option>
              )}
              {TIME_ZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* Outbound-correspondence signature */}
        <section className="grid gap-space-3 border-t border-border-light pt-space-5">
          <div>
            <h2 className="font-display text-h4 text-onyx">Correspondence signature</h2>
            <p className="text-caption text-gray-mid mt-space-1">
              Signs the emails and letters you send from Phoenix. Leave a field blank to
              use your profile details above — so this is optional, and everything keeps
              working if you never fill it in.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <div>
              <Label>Name</Label>
              <Input
                value={form.signatureName ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, signatureName: e.target.value }))}
                placeholder={profile.data?.displayName ?? "Your display name"}
              />
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={form.signatureTitle ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, signatureTitle: e.target.value }))}
                placeholder={profile.data?.jobTitle ?? "Your job title"}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.signaturePhone ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, signaturePhone: e.target.value }))}
                placeholder={profile.data?.phoneNumber ?? "Your phone number"}
              />
            </div>
            <div>
              <Label>Reply-to email</Label>
              <Input
                type="email"
                value={form.signatureEmail ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, signatureEmail: e.target.value }))}
                placeholder={profile.data?.email ?? "Your sign-in address"}
              />
            </div>
          </div>

          <div>
            <Label>Custom block</Label>
            <Textarea
              rows={4}
              value={form.signatureBlock ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, signatureBlock: e.target.value }))}
              placeholder="Leave blank to build the block from the fields above."
            />
            <div className="text-caption text-gray-mid mt-space-1">
              Anything you type here replaces the whole block, exactly as written.
            </div>
          </div>

          {/*
            The SAVED signature, straight from the server. Deliberately not
            re-composed from the fields above while you type: the server owns the
            one composition rule, and a second one here would drift and make this
            preview a quiet lie. It refreshes when you save.
          */}
          {profile.data?.signatureBlockPreview && (
            <div>
              <Label>Currently signing as</Label>
              <pre className="font-body text-small text-onyx bg-white-warm border border-border-light rounded-md p-space-3 whitespace-pre-wrap">
                {profile.data.signatureBlockPreview}
              </pre>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export function PhotoDisplay({
  src, name, size = 80,
}: { src?: string | null; name: string; size?: number }) {
  const initials = name
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const ring = { width: size, height: size };
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        style={ring}
        className="rounded-pill object-cover border-[2px] border-gold"
      />
    );
  }
  return (
    <span
      style={ring}
      className="rounded-pill bg-gold text-onyx flex items-center justify-center font-display font-bold border-[2px] border-gold"
    >
      <span style={{ fontSize: Math.round(size * 0.33) }}>{initials}</span>
    </span>
  );
}
